import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
  Select,
  StatCard,
  ConfirmationModal,
} from '@azyntor-labs/nexus-ui';
import { usersApi } from '@/api/endpoints';
import { User, UserRole } from '@/types';
import { format } from 'date-fns';
import { toast } from '@/utils/toast';

export const UserManagement = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    role: 'student' as UserRole,
    password: '',
    is_active: true,
  });

  const { data: usersResponse, isLoading } = useQuery({
    queryKey: ['users', { page, per_page: perPage, role: roleFilter }],
    queryFn: () => usersApi.list({
      page,
      per_page: perPage,
      role: roleFilter || undefined
    }),
  });

  const users = Array.isArray(usersResponse?.data?.data)
    ? usersResponse.data.data
    : [];
  const totalPages = usersResponse?.data?.meta?.total_pages || 1;
  const total = usersResponse?.data?.meta?.total || 0;

  const filteredUsers = users.filter((user: User) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower)
    );
  });

  const createUserMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.create(data),
    onSuccess: (response: any) => {
      // Show message about sync delay
      const message = response?.data?.message || 'User created successfully. They will appear shortly after sync.';
      toast.success(message);
      setIsAddModalOpen(false);
      resetForm();

      // Poll for user after 15 seconds
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }, 15000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create user');
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) =>
      usersApi.update(id, data),
    onSuccess: (response: any) => {
      // Check if role was updated (may need sync)
      const message = response?.data?.message || 'User updated successfully';
      toast.success(message);
      setIsEditModalOpen(false);
      setSelectedUser(null);
      resetForm();

      // Refresh list after potential sync delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }, 3000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: ({ id, permanent }: { id: string; permanent?: boolean }) =>
      usersApi.delete(id, permanent),
    onSuccess: (response: any) => {
      const message = response?.data?.message || 'User deleted successfully';
      toast.success(message);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);

      // Refresh list after sync delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      }, 3000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      username: '',
      first_name: '',
      last_name: '',
      role: 'student',
      password: '',
      is_active: true,
    });
  };

  const handleAddUser = () => {
    setIsAddModalOpen(true);
    resetForm();
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      username: user.email.split('@')[0],
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      password: '',
      is_active: user.is_active,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.username || !formData.first_name || !formData.last_name || !formData.password) {
      toast.error('Please fill in all required fields (email, username, first name, last name, password)');
      return;
    }
    createUserMutation.mutate(formData);
  };

  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    const updateData: Partial<User> = {
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      role: formData.role,
      is_active: formData.is_active,
    };

    updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
  };

  const handleConfirmDelete = (permanent: boolean = false) => {
    if (!selectedUser) return;
    deleteUserMutation.mutate({ id: selectedUser.id, permanent });
  };

  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case 'super_admin':
        return 'danger';
      case 'admin':
        return 'warning';
      case 'teacher':
        return 'teal';
      case 'proctor':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'student', label: 'Student' },
    { value: 'proctor', label: 'Proctor' },
  ];

  const roleOptionsForForm = roleOptions.filter(opt => opt.value !== '');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          <p className="text-white/50 mt-1">Manage users and their roles</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddUser}>
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={total}
          icon={<Users />}
          color="teal"
        />
        <StatCard
          title="Active Users"
          value={users.filter((u: User) => u.is_active).length}
          icon={<Users />}
          color="green"
        />
        <StatCard
          title="Teachers"
          value={users.filter((u: User) => u.role === 'teacher').length}
          icon={<Users />}
          color="orange"
        />
        <StatCard
          title="Students"
          value={users.filter((u: User) => u.role === 'student').length}
          icon={<Users />}
          color="teal"
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
            />
            <div className="flex items-center text-sm text-white/50">
              <Filter className="w-4 h-4 mr-2" />
              Showing {filteredUsers.length} of {total} users
            </div>
          </div>
        </div>

        {/* Users Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/60">Loading users...</p>
          </div>
        ) : filteredUsers.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Role</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Created</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user: User) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium text-white">
                        {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-white/60">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
                        {user.role ? user.role.replace('_', ' ') : 'No Role'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'success' : 'danger'} size="sm" dot>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white/50">
                      {format(new Date(user.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Edit2 className="w-3 h-3" />}
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="w-3 h-3" />}
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
              <div className="text-sm text-white/50">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 mb-3">No users found</p>
            {searchTerm && (
              <p className="text-sm text-white/30">Try adjusting your search or filters</p>
            )}
          </div>
        )}
      </Card>

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New User"
        subtitle="Create a new user account"
        size="lg"
      >
        <form onSubmit={handleSubmitCreate}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <Input
                label="Last Name"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                label="Username"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                helperText="Minimum 3 characters"
              />
            </div>
            <Input
              label="Password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              helperText="Minimum 8 characters"
            />
            <Select
              label="Role"
              required
              options={roleOptionsForForm}
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            />
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/20 focus:ring-offset-0"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-white/70">
                Active
              </label>
            </div>
          </div>
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createUserMutation.isPending}>
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit User"
        subtitle="Update user information"
        size="lg"
      >
        <form onSubmit={handleSubmitUpdate}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <Input
                label="Last Name"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
            <Input
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <Select
              label="Role"
              required
              options={roleOptionsForForm}
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
            />
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/20 focus:ring-offset-0"
              />
              <label htmlFor="edit_is_active" className="text-sm font-medium text-white/70">
                Active
              </label>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">
                Note: Password cannot be changed here. Use the Change Password feature for password updates.
              </p>
            </div>
          </div>
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateUserMutation.isPending}>
              {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => handleConfirmDelete(false)}
        title="Remove User?"
        message={`Are you sure you want to remove ${selectedUser?.first_name} ${selectedUser?.last_name} from the Exam system? Smart deletion: If this user has access to other services, only their Exam access will be revoked. Warning: Changes will sync across all systems.`}
        variant="danger"
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
};
